/**
 * Enhanced Browser Notifications Utility
 * Comprehensive wrapper for native browser notifications with Firebase integration
 */

import {
  getFCMToken,
  onForegroundMessage as onFirebaseForegroundMessage,
  isNotificationSupported as checkFirebaseSupport,
  getNotificationPermission as getFirebasePermission,
  getDeviceId,
  getDeviceInfo,
  isIOS,
  isInstalledPWA,
  canReceivePush,
  showNotificationViaServiceWorker,
} from "../config/firebase";

// ============================================================================
// Permission Management
// ============================================================================

/**
 * Check if browser supports notifications
 */
export const isNotificationSupported = () => {
  return checkFirebaseSupport();
};

/**
 * Get current notification permission status
 */
export const getPermissionStatus = () => {
  const status = getFirebasePermission();

  switch (status) {
    case "granted":
      return "granted";
    case "denied":
      return "denied";
    case "default":
      return "default";
    default:
      return "unsupported";
  }
};

/**
 * Request notification permission with enhanced error handling
 */
export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    return {
      granted: false,
      permission: "unsupported",
      error: "Notifications not supported",
    };
  }

  const currentStatus = getPermissionStatus();

  // If already granted, return success
  if (currentStatus === "granted") {
    return { granted: true, permission: "granted" };
  }

  // If permanently denied, return error
  if (currentStatus === "denied") {
    return {
      granted: false,
      permission: "denied",
      error: "Notifications permanently denied",
    };
  }

  try {
    // Only request permission — don't try to get FCM token here.
    // The hook handles token registration separately.
    const permission = await Notification.requestPermission();

    if (permission === "granted") {
      return { granted: true, permission: "granted" };
    } else {
      return {
        granted: false,
        permission: permission,
        error: "Permission request denied by user",
      };
    }
  } catch (error) {
    console.error("Error requesting notification permission:", error);
    return {
      granted: false,
      permission: "denied",
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Check if permission can be requested (not permanently denied)
 */
export const canRequestPermission = () => {
  const status = getPermissionStatus();
  return status === "default" || status === "granted";
};

/**
 * Get human-readable permission status
 */
export const getPermissionStatusText = () => {
  const status = getPermissionStatus();

  switch (status) {
    case "granted":
      return "Notifications are enabled";
    case "denied":
      return "Notifications are blocked";
    case "default":
      return "Notifications not configured";
    default:
      return "Notifications not supported";
  }
};

// ============================================================================
// Token Management
// ============================================================================

/**
 * Get FCM registration token
 */
export const getRegistrationToken = async () => {
  try {
    const token = await getFCMToken();
    return token;
  } catch (error) {
    console.error("Error getting FCM token:", error);
    return null;
  }
};

/**
 * Store token in localStorage with session association
 */
export const storeToken = (token, sessionId) => {
  try {
    localStorage.setItem("fcmToken", token);
    localStorage.setItem("fcmTokenSession", sessionId);
    localStorage.setItem("fcmTokenTimestamp", Date.now().toString());
  } catch (error) {
    console.error("Error storing token:", error);
  }
};

/**
 * Retrieve stored token
 */
export const getStoredToken = () => {
  try {
    const token = localStorage.getItem("fcmToken");
    const sessionId = localStorage.getItem("fcmTokenSession");
    const timestamp = localStorage.getItem("fcmTokenTimestamp");

    if (!token || !sessionId || !timestamp) {
      return null;
    }

    // Check if token is older than 7 days (refresh recommendation)
    const tokenAge = Date.now() - parseInt(timestamp, 10);
    const sevenDays = 7 * 24 * 60 * 60 * 1000;

    if (tokenAge > sevenDays) {
      console.warn("FCM token is older than 7 days, consider refreshing");
    }

    return token;
  } catch (error) {
    console.error("Error retrieving stored token:", error);
    return null;
  }
};

/**
 * Clear stored token because the user turned notifications OFF.
 *
 * Sets notificationsManuallyDisabled so auto-registration doesn't immediately
 * undo the user's choice. Do NOT use this for session end — see
 * clearStoredTokenForSessionEnd.
 */
export const clearStoredToken = () => {
  try {
    localStorage.removeItem("fcmToken");
    localStorage.removeItem("fcmTokenSession");
    localStorage.removeItem("fcmTokenTimestamp");
    localStorage.setItem("notificationsManuallyDisabled", "true");
  } catch (error) {
    console.error("Error clearing stored token:", error);
  }
};

/**
 * Clear stored token because the guest's session ended.
 *
 * Deliberately does not set notificationsManuallyDisabled: the session ending is
 * not the user opting out, and on a shared or re-used device that flag would
 * silently suppress notifications for the next guest.
 */
export const clearStoredTokenForSessionEnd = () => {
  try {
    localStorage.removeItem("fcmToken");
    localStorage.removeItem("fcmTokenSession");
    localStorage.removeItem("fcmTokenTimestamp");
  } catch (error) {
    console.error("Error clearing stored token:", error);
  }
};

/**
 * Check if we have a valid stored token
 */
export const hasValidStoredToken = (sessionId) => {
  const storedSession = localStorage.getItem("fcmTokenSession");
  const token = localStorage.getItem("fcmToken");
  
  // Check if token exists and belongs to current session
  return storedSession === sessionId && !!token;
};

// ============================================================================
// Foreground Message Handling
// ============================================================================

/**
 * Subscribe to foreground messages with enhanced callback
 */
export const onForegroundMessage = (callback) => {
  return onFirebaseForegroundMessage((payload) => {
    const enhancedPayload = enhanceFCMPayload(payload);
    callback(enhancedPayload);
  });
};

/**
 * Enhanced FCM message payload
 */
export const enhanceFCMPayload = (payload) => {
  const notification = payload.notification || {};
  const data = payload.data || {};

  return {
    messageId: payload.messageId || `msg-${Date.now()}`,
    from: payload.from || "",
    collapseKey: payload.collapseKey || "",
    notification: {
      title: notification.title || "New Notification",
      body: notification.body || "",
      tag: notification.tag,
      icon: notification.icon,
      clickAction: notification.clickAction,
    },
    data,
    timestamp: new Date(),
  };
};

// ============================================================================
// Browser Notification Display
// ============================================================================

/**
 * Show a browser notification with enhanced options.
 * Uses service worker showNotification() which works on both desktop and mobile.
 */
export const showBrowserNotification = (title, options = {}) => {
  if (!isNotificationSupported()) {
    console.warn("Browser notifications not supported");
    return null;
  }

  if (getPermissionStatus() !== "granted") {
    console.warn("Notification permission not granted");
    return null;
  }

  const defaultOptions = {
    icon: "/android-chrome-192x192.png",
    badge: "/favicon-32x32.png",
    vibrate: [200, 100, 200],
    requireInteraction: false,
    silent: false,
    tag: `notification-${Date.now()}`,
    ...options,
  };

  // Use service worker notification (works on mobile)
  showNotificationViaServiceWorker(title, defaultOptions);

  return true;
};

/**
 * Show notification for request status update
 */
export const showRequestUpdateNotification = (request) => {
  // Use the same format as backend notifications
  const statusConfig = {
    'In Progress': { emoji: '🔄', title: 'Request In Progress' },
    'Completed': { emoji: '✅', title: 'Request Completed' },
    'Cancelled': { emoji: '❌', title: 'Request Cancelled' },
    'Rejected': { emoji: '❌', title: 'Request Rejected' },
    'Escalated': { emoji: '⚠️', title: 'Request Escalated' },
    'Pending': { emoji: '📝', title: 'Request Received' },
  };

  const config = statusConfig[request.status] || { emoji: '🔔', title: 'Request Update' };
  const serviceTypeName = request.serviceType?.replace('-Service', '').replace('FNB', 'Food Order').replace('Housekeeping', 'Housekeeping').replace('Maintenance', 'Maintenance') || 'request';
  
  const title = `${config.emoji} ${config.title}`;
  const body = `Your ${serviceTypeName} request is now ${request.status}`;

  return showBrowserNotification(title, {
    body,
    tag: `request-${request._id}`,
    data: { requestId: request._id, url: "/my-requests" },
    onClickAction: () => {
      window.location.href = "/my-requests";
    },
  });
};

/**
 * Show notification for new message
 */
export const showMessageNotification = (
  message,
  senderName,
  conversationId,
) => {
  return showBrowserNotification("💬 New Message", {
    body: `${senderName}: ${message}`,
    tag: `message-${conversationId}`,
    data: { conversationId, url: "/my-requests" },
    onClickAction: () => {
      window.location.href = "/my-requests";
    },
  });
};

/**
 * Show notification for emergency alert
 */
export const showEmergencyNotification = (title, body, url) => {
  return showBrowserNotification(`🚨 ${title}`, {
    body,
    tag: "emergency-notification",
    requireInteraction: true,
    vibrate: [500, 200, 500, 200, 500],
    data: { url },
    onClickAction: () => {
      if (url) {
        window.location.href = url;
      }
    },
  });
};

// ============================================================================
// Prompt Management
// ============================================================================

/**
 * Check if user has dismissed notification prompt
 */
export const hasUserDismissedPrompt = () => {
  const dismissed = localStorage.getItem("notificationPromptDismissed");

  if (!dismissed) {
    return false;
  }

  const dismissedDate = new Date(dismissed);
  const now = new Date();

  // Check if dismissal has expired (7 days)
  return dismissedDate > now;
};

/**
 * Mark notification prompt as dismissed
 */
export const dismissNotificationPrompt = (days = 7) => {
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + days);
  localStorage.setItem("notificationPromptDismissed", expiryDate.toISOString());
};

/**
 * Clear dismissal (show prompt again)
 */
export const clearDismissal = () => {
  localStorage.removeItem("notificationPromptDismissed");
};

/**
 * Check if we should show the notification prompt
 */
export const shouldShowNotificationPrompt = (sessionId) => {
  if (!sessionId) return false;
  if (getPermissionStatus() === "granted") return false;
  if (hasUserDismissedPrompt()) return false;
  return isNotificationSupported();
};

// ============================================================================
// Device Information
// ============================================================================

/**
 * Get device ID
 */
export const getDeviceIdentifier = () => {
  return getDeviceId();
};

/**
 * Get enhanced device information
 */
export const getEnhancedDeviceInfo = () => {
  return {
    deviceId: getDeviceId(),
    platform: "web",
    deviceInfo: getDeviceInfo(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

// ============================================================================
// Error Handling
// ============================================================================

/**
 * Handle permission denial with helpful guidance
 */
export const handlePermissionDenial = () => {
  const status = getPermissionStatus();

  if (status === "denied") {
    return {
      message: "Notifications have been blocked for this site.",
      guidance: [
        "Click the lock icon in your browser's address bar",
        'Find "Notifications" and change it to "Allow"',
        "Refresh the page to try again",
      ],
      canRetry: false,
    };
  }

  return {
    message: "Unable to request notification permission.",
    guidance: [
      "Please try using a different browser",
      "Check your browser settings",
    ],
    canRetry: true,
  };
};

/**
 * Log notification error with context
 */
export const logNotificationError = (context, error) => {
  console.error(`[Notifications] ${context}:`, {
    error: error instanceof Error ? error.message : error,
    permissionStatus: getPermissionStatus(),
    isSupported: isNotificationSupported(),
    timestamp: new Date().toISOString(),
  });
};

// Re-export mobile detection helpers
export { isIOS, isInstalledPWA, canReceivePush };
